import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { fetchTariff, updateTariffPricebook } from "@/lib/api";
import { PricingForm } from "@/components/PricingForm";
import {
  type PricingFormValues, emptyPricingForm, pricebookToValues, valuesToPricebook,
  isPricebookFree, validatePricingForm,
} from "@/lib/pricingForm";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useT } from "@/app/localization";
import { errorMessage } from "@/app/serverErrors";

interface TariffEditorProps {
  unitId: string;
  onSuccess?: () => void;
  inline?: boolean;
}

/**
 * Charger DEFAULT-tariff editor. Uses the ONE shared <PricingForm> + lib/pricingForm
 * serialization — identical fields/validation/pricebook to the Pricing Profiles form.
 * The ONLY difference is the save target: this writes the charger's default pricebook.
 */
export function TariffEditor({ unitId, onSuccess, inline }: TariffEditorProps) {
  const t = useT();
  const tariffQ = useQuery({
    queryKey: ["unit", unitId, "tariff"],
    queryFn: () => fetchTariff(unitId),
    enabled: !!unitId,
  });

  const [form, setForm] = useState<PricingFormValues>(emptyPricingForm());

  useEffect(() => {
    if (!tariffQ.data) return;
    setForm(pricebookToValues(tariffQ.data.pricebook));
  }, [tariffQ.data]);

  const mutation = useMutation({
    mutationFn: () => {
      const pricebook = valuesToPricebook(form);
      return updateTariffPricebook(unitId, isPricebookFree(pricebook) ? "free" : "paid", pricebook);
    },
    onSuccess: () => {
      toast.success(t("billing.tariffs.updated"));
      tariffQ.refetch();
      onSuccess?.();
    },
    onError: (e) => toast.error(errorMessage(t, e, { family: "unit_settings", fallback: "billing.tariffs.update_failed" })),
  });

  // The form's own validation message is shown as is; only request failures go through errorMessage.
  const save = () => {
    const err = validatePricingForm(form, t);
    if (err) { toast.error(err); return; }
    mutation.mutate();
  };

  if (tariffQ.isLoading) return <div className="text-sm opacity-60">{t("billing.tariffs.loading")}</div>;
  if (tariffQ.isError) return <div className="text-sm text-destructive">{t("billing.tariffs.load_failed")}</div>;

  const content = (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        {t("billing.tariffs.default_rate_help")}
      </p>
      <PricingForm value={form} onChange={setForm} idPrefix="tariff" />
      <div className="flex justify-end">
        <Button onClick={save} disabled={mutation.isPending}>
          {mutation.isPending ? t("common.state.saving") : t("common.action.save")}
        </Button>
      </div>
    </div>
  );

  if (inline) return <div className="border-t pt-4 space-y-4">{content}</div>;

  return (
    <Card>
      <CardHeader><CardTitle>{t("billing.tariffs.title")}</CardTitle></CardHeader>
      <CardContent className="flex-1 flex flex-col space-y-4">{content}</CardContent>
    </Card>
  );
}
